import React, { useState, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import useWindowEvent from 'hooks/useWindowEvent';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { useClickAway } from 'react-use';
import Icon from 'components/icon';

export type DropdownMenuProps = {
  text: string;
  menu: Array<{
    href: string;
    name: string;
  }>;
  prefix?: string;
};

enum LanguageTypes {
  da = 'da-dk',
  de = 'de-de',
  'en-us' = 'en-us',
}

interface DropdownButtonProps {
  show: boolean;
}

const DropdownMenuWrapper = styled.div<DropdownButtonProps>`
  position: relative;
  z-index: 50;
  display: flex;
  height: 100%;

  ${(props) =>
    props.show &&
    `
      background-color: var(--new-dropdown-input-background);
  `}
`;

const DropdownButton = styled.button<DropdownButtonProps>`
  position: relative;
  padding: 0.5rem;
  font-size: 1rem;
  outline: none;
  background: transparent;
  cursor: pointer;

  .dropdown-text {
    padding-right: 0.25rem;
    font-family: 'Alliance No.2';
    font-size: 22px;
    line-height: 26.06px;
  }

  &:hover {
    .dropdown-text {
      position: relative;
    }
  }

  ${(props) =>
    props.show
      ? `
      .dropdown-text {
        position: relative;
        background-color: var(--new-dropdown-input-background);
      }

      .dropdown-wrapper {
        background-color: var(--new-dropdown-input-background);
      }
        `
      : `&:hover {
          .dropdown-text {
            position: relative;
            &:after {
              content: '';
              display: block;
              position: absolute;
              border-bottom: 2px solid var(--text);
              left: 0;
              right: 0;
              bottom: -0.125rem;
            }
          }
  }`}
`;

const DropdownButtonText = styled.span`
  font-size: 1rem;
  color: var(--new-text-regular);
`;

const Dropdown = styled.div`
  position: absolute;
  z-index: 10;
  background-color: var(--new-dropdown-input-background);
  border: 1px solid var(--new-border);
  box-shadow: 0px 20px 7.5px 0px rgba(0, 0, 0, 0.1);
  width: 220px;
  top: 3.3rem;

  div:nth-child(even) {
    background-color: #def5fe;
  }

  ul {
    display: contents !important;
  }
`;

const DropdownItem = styled.li`
  margin: 0 0;
`;

const DropdownLink = styled.a`
  display: flex;
  padding: 0.5rem 1rem;
  text-decoration: none;
  color: var(--new-text-regular);
  font-size: var(--fontsize-base);

  &:hover {
    text-decoration: underline;
  }
`;

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ text, menu, prefix }) => {
  const { locale } = useRouter();
  const [show, setShow] = useState(false);
  const keys = useMemo(() => menu.map(() => uuidv4()), [menu]);
  const ref = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: { keyCode: number }) => {
    if (e.keyCode === 27) {
      setShow(false);
    }
  };

  useClickAway(ref, () => {
    setShow(false);
  });
  useWindowEvent('keydown', handleKeyDown);

  const checkPrefix = () => {
    if (prefix) {
      return `${prefix}${LanguageTypes[locale.toLowerCase()]}`;
    }

    return '';
  };

  return (
    <DropdownMenuWrapper ref={ref} show={show}>
      <DropdownButton
        show={show}
        onClick={(e) => {
          e.stopPropagation();
          show ? setShow(false) : setShow(true);
        }}
      >
        <DropdownButtonText className="dropdown-text">{text}</DropdownButtonText>
        {show ? <Icon name="chevron-up-light" /> : <Icon name="chevron-down-light" />}
      </DropdownButton>

      {show && (
        <Dropdown>
          <ul aria-labelledby="dropdownDefaultButton">
            {menu?.map((link, index) => (
              <DropdownItem key={keys[index]}>
                {/* <DropdownLink href={`${checkPrefix()}${link.href}`}>{link.name}</DropdownLink> */}
                <DropdownLink href={`${link.href}`}>{link.name}</DropdownLink>
              </DropdownItem>
            ))}
          </ul>
        </Dropdown>
      )}
    </DropdownMenuWrapper>
  );
};

export default DropdownMenu;
